import { DatePipe } from '@angular/common'
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
} from '@ng-bootstrap/ng-bootstrap'
import { Subscription } from 'rxjs'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { LiquidAssetService } from 'src/app/services/liquid-asset/liquid-asset.service'

@Component({
  selector: 'liquid-asset',
  templateUrl: './liquid-asset.component.html',
  styleUrls: ['./liquid-asset.component.scss'],
  providers: [],
})
export class LiquidAssetComponent implements OnInit, OnDestroy {
  @Input() fundId: string
  @ViewChild('liquidityAssetsAriaLiveRegion', { static: false })
  public liquidityAssetsAriaLiveRegion: ElementRef
  @ViewChild('dailyLiquidAssetsDialogContent', { static: false })
  public dailyLiquidAssetsDialogContent: TemplateRef<any>
  @ViewChild('weeklyLiquidAssetsDialogContent', { static: false })
  public weeklyLiquidAssetsDialogContent: TemplateRef<any>
  @ViewChild('dailyMarketValueDialogContent', { static: false })
  public dailyMarketValueDialogContent: TemplateRef<any>
  @ViewChild('dailyCashFlowDialogContent', { static: false })
  public dailyCashFlowDialogContent: TemplateRef<any>

  //AEM content variables
  dailyLiquidAssets: AEMChunk
  weeklyLiquidAssets: AEMChunk
  dailyMarketValue: AEMChunk
  dailyNetShareholderCashFlow: AEMChunk
  aemContentSub: Subscription

  /* eslint max-params: [2, 6] */
  constructor(
    public readonly liquidAssetService: LiquidAssetService,
    private readonly calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private readonly centralContentService: CentralContentService,
  ) {
    this.datePipe = new DatePipe('en-US')
    this.fromDate = calendar.getPrev(calendar.getToday(), 'm', this.maxYears)
    const prevdate = 2
    this.toDate = calendar.getPrev(calendar.getToday(), 'd', prevdate)
    this.validateDates()
  }

  readonly maxPageSize: number = 10
  liquidAssetModel: any
  public datePipe: DatePipe
  currentItems: Array<any>
  items: Array<any>
  displayedItems: Array<any>
  pageSize: number
  page: number
  hoveredDate: NgbDate | null = null
  fromDate: NgbDate | null
  toDate: NgbDate | null
  maxFromDate: NgbDate | null
  maxToDate: NgbDate | null
  fromDateFilter: string
  toDateFilter: string
  readonly maxYears: number = 6
  pageNumberList = []
  slectedPage: number
  readonly timeConvertUTC: number = 4

  scrollBarPosition = 0
  showRightButton = true
  tableContainer: HTMLElement
  innerWidth: number

  ngOnInit(): void {
    this.findScreenSize()
    this.pageSize = this.maxPageSize
    this.page = 1
    if (this.fundId) {
      this.liquidAssetService
        .getLiquidAsset(this.fundId)
        .subscribe((params) => {
          this.currentItems = []
          this.items = []
          params.item?.forEach((element) => {
            this.currentItems.push(element)
            this.items.push(element)
            this.setPageNumber()
            this.calltableScrollEvent()
          })
        })
    }

    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    this.dailyLiquidAssets = this.centralContentService.getContentByBody(
      'fund_definitions_daily_liquid_assets',
      content,
    )
    this.weeklyLiquidAssets = this.centralContentService.getContentByBody(
      'fund_definitions_weekly_liquid_assets',
      content,
    )
    this.dailyMarketValue = this.centralContentService.getContentByBody(
      'fund_definitions_daily_market_value',
      content,
    )
    this.dailyNetShareholderCashFlow =
      this.centralContentService.getContentByBody(
        'fund_definitions_daily_net_shareholder_cash_flow',
        content,
      )
  }

  parseDate(asOfDate: string) {
    if (asOfDate) {
      return (
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        asOfDate?.split('-')[1] +
        '/' +
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        asOfDate?.split('-')[2].split('T')[0] +
        '/' +
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        asOfDate?.split('-')[0]
      )
    } else {
      return null
    }
  }

  onFromDateSelection(date: NgbDate) {
    if (this.isInside(date)) {
      this.fromDate = date
    }
    if (this.fromDate && this.toDate) {
      this.onDateSelection()
    }
  }

  onToDateSelection(date: NgbDate) {
    if (this.isInside(date)) {
      this.toDate = date
    }
    if (this.fromDate && this.toDate) {
      this.onDateSelection()
    }
  }

  onDateSelection() {
    this.fromDateFilter =
      this.fromDate.year +
      '-' +
      this.beautifyDayMonth(String(this.fromDate.month)) +
      '-' +
      this.beautifyDayMonth(String(this.fromDate.day))
    this.toDateFilter =
      this.toDate.year +
      '-' +
      this.beautifyDayMonth(String(this.toDate.month)) +
      '-' +
      this.beautifyDayMonth(String(this.toDate.day))
    const fromDateComp = new Date(this.fromDateFilter)
    fromDateComp.setHours(fromDateComp.getHours() + this.timeConvertUTC)
    const toDateComp = new Date(this.toDateFilter)
    toDateComp.setHours(toDateComp.getHours() + this.timeConvertUTC)
    this.isInDateRange(fromDateComp, toDateComp)
  }

  addElement(element) {
    this.currentItems.push(element)
    return this.currentItems
  }

  isInDateRange(date1: Date, date2: Date) {
    this.currentItems = []
    let recordDate: Date
    this.items.forEach((element) => {
      recordDate = new Date(
        element?.['effectiveDate']
          ? element['effectiveDate']
          : '2011-07-30T00:00:00-04:00',
      )
      this.currentItems =
        date1 <= recordDate && recordDate <= date2
          ? this.addElement(element)
          : this.currentItems
    })
    this.setPageNumber()
  }

  beautifyDayMonth(date: string) {
    if (date.length === 1) {
      date = '0' + date
    }
    return date
  }

  isInside(date: NgbDate) {
    return (
      this.toDate && date.after(this.maxFromDate) && date.before(this.maxToDate)
    )
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input)
    return parsed && this.calendar.isValid(NgbDate.from(parsed))
      ? NgbDate.from(parsed)
      : currentValue
  }

  getPageId(e) {
    this.page = e
    this.displayedItems = this.currentItems.slice(
      (this.page - 1) * this.maxPageSize,
      this.page * this.maxPageSize,
    )
  }

  /*eslint max-statements: ["error", 20]*/
  setPageNumber() {
    const pages: number = Math.ceil(this.currentItems.length / this.maxPageSize)
    let results: string
    let resStart = 1
    let restEnd: number = this.maxPageSize
    this.pageNumberList = []

    if (this.currentItems.length === 0) {
      results = 0 + '-' + this.currentItems.length
      this.pageNumberList.push(
        results + ' of ' + this.currentItems.length + '       Page 1',
      )
    } else if (this.currentItems.length < this.maxPageSize + 1) {
      results = resStart + '-' + this.currentItems.length
      this.pageNumberList.push(
        results + ' of ' + this.currentItems.length + '       Page 1',
      )
    } else {
      for (let i = 1; i < pages + 1; i++) {
        if (i === pages) {
          results = resStart + '-' + this.currentItems.length
        } else {
          results = resStart + '-' + restEnd
        }
        this.pageNumberList.push(
          results + ' of ' + this.currentItems.length + '       Page ' + i,
        )
        resStart = resStart + this.maxPageSize
        restEnd = restEnd + this.maxPageSize
      }
    }
  }

  findScreenSize() {
    this.innerWidth = window.innerWidth
  }

  calltableScrollEvent() {
    setTimeout(() => {
      this.tableContainer = document.querySelector('.horizontal-scroll-table')
      this.tableContainer?.addEventListener('scroll', () => {
        this.scrollTableHandler()
      })
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    }, 300)
  }

  scrollTableHandler() {
    this.showRightButton =
      this.tableContainer.offsetWidth + this.tableContainer.scrollLeft >=
      this.tableContainer.scrollWidth
        ? false
        : true
    this.scrollBarPosition = this.tableContainer.scrollLeft
  }

  moveRight() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.tableContainer.scrollLeft += 150
  }

  moveLeft() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.tableContainer.scrollLeft -= 150
  }
  validateDates() {
    const fromDateDay = this.calendar.getWeekday(this.fromDate)
    const saturday = 6
    const sunday = 7
    const addday = 2
    if (fromDateDay === saturday) {
      this.fromDate = this.calendar.getNext(this.fromDate, 'd', addday)
    } else if (fromDateDay === sunday) {
      this.fromDate = this.calendar.getNext(this.fromDate, 'd')
    }

    const toDateDay = this.calendar.getWeekday(this.toDate)
    if (toDateDay >= saturday) {
      this.toDate = this.calendar.getPrev(this.toDate, 'd', addday)
    }
    this.maxFromDate = this.fromDate
    this.maxToDate = this.toDate
  }
  isDisabled = (date: NgbDate) => {
    const saturday = 6
    return this.calendar.getWeekday(date) >= saturday
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
